/* eslint-disable no-magic-numbers */
/**
 * Simplified trimbim generator for alignment models.
 *
 * Ported from the web3d TrimbimGenerator, keeping only the geometry types
 * actually used in alignment models: GraphicsContainer (line segments),
 * BillboardText (station labels), AlignmentCurve, and GridLineContainer.
 *
 * PropertySets are supported for copying alignment metadata.
 */
import { Builder, Offset } from 'flatbuffers';
import {
  BillboardText as FbsBillboardText,
  Entity as FbsEntity,
  GeometryDefinition as FbsGeometryDefinition,
  GeometryInstance as FbsGeometryInstance,
  GeometryType,
  GraphicsContainer as FbsGraphicsContainer,
  LayoutType,
  Line3f as FbsLine3f,
  LineStyle,
  Model as FbsModel,
  ModelEntities as FbsModelEntities,
  ModelGeometry as FbsModelGeometry,
  ModelProperties as FbsModelProperties,
  Placement3 as FbsPlacement3,
  PropertySet as FbsPropertySet,
  PropertySetDefinition as FbsPropertySetDefinition,
  Sidedness,
  SinglePropertyDefinition as FbsSinglePropertyDefinition,
  SurfaceMaterial as FbsSurfaceMaterial,
  TextStyle as FbsTextStyle,
  AlignmentType,
  Arc3f as FbsArc3f,
  IndexedVertex as FbsIndexedVertex,
  TriangleMesh as FbsTriangleMesh,
  Vec3f as FbsVec3f,
} from '@technology/web3d-plugin-trimbim/dist/esnext/TrimBim_generated';

// ---------------------------------------------------------------------------
// Internal types
// ---------------------------------------------------------------------------

interface Vec3 {
  x: number;
  y: number;
  z: number;
}

interface LineSeg {
  start: Vec3;
  end: Vec3;
}

interface ArcSeg {
  start: Vec3;
  mid: Vec3;
  end: Vec3;
  normal: Vec3;
}

interface MaterialDef {
  r: number;
  g: number;
  b: number;
  a: number;
  textureId: number;
  sidedness: number;
  lineStyle: number;
  metallic: number;
  roughness: number;
}

interface TextStyleDef {
  font: string;
  size: number;
  options: number;
  verticalAlign: number;
  horizontalAlign: number;
  rotationAngle: number;
  layout: number;
  alignment: number;
  maxAutoScale: number;
  verticalDistanceToObject: number;
}

interface BillboardTextDef {
  textStyleId: number;
  text: string;
}

interface TriangleMeshDef {
  positions: Vec3[];
  normals: Vec3[];
  vertices: Array<{ positionIndex: number; normalIndex: number }>;
  indices: number[];
}

interface GraphicsContainerDef {
  lineSegments: LineSeg[];
  arcs: ArcSeg[];
}

interface GeomDef {
  type: GeometryType;
  typeIndex: number;
  bbMin: Vec3;
  bbMax: Vec3;
}

interface InstanceDef {
  localPlacementId: number;
  entityId: number;
  definitionId: number;
  surfaceMaterialId: number;
  layerId: number;
}

interface EntityDef {
  idType: number;
  idIndex: number;
  classIndex: number;
  globalOrigin: Vec3;
  xAxis: Vec3;
  yAxis: Vec3;
}

interface PropertySetBindingDef {
  definition: number;
  values: number[];
  entities: number[];
}

interface PropertySetDefinitionDef {
  name: number;
  properties: Array<{ name: number; type: number }>;
}

// ---------------------------------------------------------------------------
// Generator
// ---------------------------------------------------------------------------

export class AlignmentTrimbimGenerator {
  // Geometry pools (type-specific arrays)
  private readonly graphicsContainers: GraphicsContainerDef[] = [];
  private readonly billboardTexts: BillboardTextDef[] = [];
  private readonly triangleMeshes: TriangleMeshDef[] = [];

  // Shared pools
  private readonly materials: MaterialDef[] = [];
  private readonly textStyles: TextStyleDef[] = [];
  private readonly placements: Array<{
    origin: Vec3;
    xAxis: Vec3;
    yAxis: Vec3;
  }> = [];
  private readonly definitions: GeomDef[] = [];
  private readonly instances: InstanceDef[] = [];
  private readonly layers: string[] = [];
  private readonly entities: EntityDef[] = [];

  // Property system
  private readonly propertySetBindings: PropertySetBindingDef[] = [];
  private readonly propertySetDefinitions: PropertySetDefinitionDef[] = [];
  private readonly propertySetNames: string[] = [];
  private readonly propertyNames: string[] = [];
  private readonly stringValues: string[] = [];

  // String identifiers for entities
  private readonly stringIdentifiers: string[] = [];

  // Entity classes (cloned from source)
  private readonly entityClasses: string[] = [];

  // Layer default visibility (cloned from source)
  private readonly layerVisibility: boolean[] = [];

  // ── Public API ──────────────────────────────────────────────────────

  public addMaterial(mat: MaterialDef): number {
    this.materials.push(mat);
    return this.materials.length - 1;
  }

  public addTextStyle(ts: TextStyleDef): number {
    this.textStyles.push(ts);
    return this.textStyles.length - 1;
  }

  public addPlacement(
    origin: Vec3,
    xAxis: Vec3 = { x: 1, y: 0, z: 0 },
    yAxis: Vec3 = { x: 0, y: 1, z: 0 }
  ): number {
    this.placements.push({ origin, xAxis, yAxis });
    return this.placements.length - 1;
  }

  public addLayer(name: string): number {
    this.layers.push(name);
    return this.layers.length - 1;
  }

  public addEntity(entity: EntityDef): number {
    this.entities.push(entity);
    return this.entities.length - 1;
  }

  public addStringIdentifier(id: string): number {
    this.stringIdentifiers.push(id);
    return this.stringIdentifiers.length - 1;
  }

  public addEntityClass(name: string): number {
    this.entityClasses.push(name);
    return this.entityClasses.length - 1;
  }

  public addLayerVisibility(visible: boolean): void {
    this.layerVisibility.push(visible);
  }

  public addGraphicsContainer(
    gc: GraphicsContainerDef,
    bbMin: Vec3 = { x: 0, y: 0, z: 0 },
    bbMax: Vec3 = { x: 0, y: 0, z: 0 }
  ): number {
    const typeIndex = this.graphicsContainers.length;
    this.graphicsContainers.push(gc);
    this.definitions.push({
      type: GeometryType.GraphicsContainer,
      typeIndex,
      bbMin,
      bbMax,
    });
    return this.definitions.length - 1;
  }

  public addBillboardText(
    bt: BillboardTextDef,
    bbMin: Vec3 = { x: 0, y: 0, z: 0 },
    bbMax: Vec3 = { x: 0, y: 0, z: 0 }
  ): number {
    const typeIndex = this.billboardTexts.length;
    this.billboardTexts.push(bt);
    this.definitions.push({
      type: GeometryType.BillboardText,
      typeIndex,
      bbMin,
      bbMax,
    });
    return this.definitions.length - 1;
  }

  public addTriangleMesh(
    mesh: TriangleMeshDef,
    bbMin: Vec3 = { x: 0, y: 0, z: 0 },
    bbMax: Vec3 = { x: 0, y: 0, z: 0 }
  ): number {
    const typeIndex = this.triangleMeshes.length;
    this.triangleMeshes.push(mesh);
    this.definitions.push({
      type: GeometryType.TriangleMesh,
      typeIndex,
      bbMin,
      bbMax,
    });
    return this.definitions.length - 1;
  }

  public addInstance(inst: InstanceDef): number {
    this.instances.push(inst);
    return this.instances.length - 1;
  }

  // ── Property system ─────────────────────────────────────────────────

  public addPropertySetName(name: string): number {
    this.propertySetNames.push(name);
    return this.propertySetNames.length - 1;
  }

  public addPropertyName(name: string): number {
    this.propertyNames.push(name);
    return this.propertyNames.length - 1;
  }

  public addStringValue(value: string): number {
    this.stringValues.push(value);
    return this.stringValues.length - 1;
  }

  public addPropertySetBinding(binding: PropertySetBindingDef): void {
    this.propertySetBindings.push(binding);
  }

  public addPropertySetDefinition(def: PropertySetDefinitionDef): void {
    this.propertySetDefinitions.push(def);
  }

  // ── Generate ────────────────────────────────────────────────────────

  public generate(): ArrayBuffer {
    const b = new Builder(1024);

    // ── Properties ────────────────────────────────────────────────────
    const propertiesOffset = this.buildProperties(b);

    // ── Entities ──────────────────────────────────────────────────────
    const entitiesOffset = this.buildEntities(b);

    // ── Geometry ──────────────────────────────────────────────────────
    const geometryOffset = this.buildGeometry(b);

    // ── Top-level Model ───────────────────────────────────────────────
    const mapConversionsOffset = FbsModel.createMapConversionsVector(b, []);
    const metadataOffset = FbsModel.createMetadataVector(b, []);

    FbsModel.startModel(b);
    FbsModel.addEntities(b, entitiesOffset);
    FbsModel.addProperties(b, propertiesOffset);
    FbsModel.addGeometry(b, geometryOffset);
    FbsModel.addMapConversions(b, mapConversionsOffset);
    FbsModel.addMetadata(b, metadataOffset);
    const modelOffset = FbsModel.endModel(b);

    FbsModel.finishModelBuffer(b, modelOffset);

    return b.asUint8Array().slice().buffer;
  }

  // ── Private builders ────────────────────────────────────────────────

  private buildProperties(b: Builder): Offset {
    // String values
    const svOffsets = this.stringValues.map((v) => b.createString(v));
    const stringValuesOff = FbsModelProperties.createStringValuesVector(
      b,
      svOffsets
    );

    // Property set names
    const psnOffsets = this.propertySetNames.map((n) => b.createString(n));
    const propertySetNamesOff = FbsModelProperties.createPropertySetNamesVector(
      b,
      psnOffsets
    );

    // Property names
    const pnOffsets = this.propertyNames.map((n) => b.createString(n));
    const propertyNamesOff = FbsModelProperties.createPropertyNamesVector(
      b,
      pnOffsets
    );

    // Property set definitions
    const defOffsets = this.propertySetDefinitions.map((def) => {
      FbsPropertySetDefinition.startPropertiesVector(b, def.properties.length);
      for (let i = def.properties.length - 1; i >= 0; i--) {
        FbsSinglePropertyDefinition.createSinglePropertyDefinition(
          b,
          def.properties[i].name,
          def.properties[i].type
        );
      }
      const propsOff = b.endVector();
      FbsPropertySetDefinition.startPropertySetDefinition(b);
      FbsPropertySetDefinition.addName(b, def.name);
      FbsPropertySetDefinition.addProperties(b, propsOff);
      return FbsPropertySetDefinition.endPropertySetDefinition(b);
    });
    const definitionsOff = FbsModelProperties.createDefinitionsVector(
      b,
      defOffsets
    );

    // Property set bindings
    const bindingOffsets = this.propertySetBindings.map((binding) => {
      const valuesOff = FbsPropertySet.createValuesVector(b, binding.values);
      const entitiesOff = FbsPropertySet.createEntitiesVector(
        b,
        binding.entities
      );
      FbsPropertySet.startPropertySet(b);
      FbsPropertySet.addDefinition(b, binding.definition);
      FbsPropertySet.addValues(b, valuesOff);
      FbsPropertySet.addEntities(b, entitiesOff);
      return FbsPropertySet.endPropertySet(b);
    });
    const bindingsOff = FbsModelProperties.createPropertySetBindingsVector(
      b,
      bindingOffsets
    );

    // All child offsets must be created BEFORE startModelProperties
    const namesOff = FbsModelProperties.createNamesVector(b, []);
    const descriptionsOff = FbsModelProperties.createDescriptionsVector(b, []);
    const objectTypesOff = FbsModelProperties.createObjectTypesVector(b, []);
    const ownersOff = FbsModelProperties.createOwnersVector(b, []);
    const lengthMeasuresOff = FbsModelProperties.createLengthMeasuresVector(
      b,
      []
    );
    const areaMeasuresOff = FbsModelProperties.createAreaMeasuresVector(b, []);
    const volumeMeasuresOff = FbsModelProperties.createVolumeMeasuresVector(
      b,
      []
    );
    const massMeasuresOff = FbsModelProperties.createMassMeasuresVector(b, []);
    const angleMeasuresOff = FbsModelProperties.createAngleMeasuresVector(
      b,
      []
    );
    const doubleValuesOff = FbsModelProperties.createDoubleValuesVector(b, []);
    const dateTimeValuesOff = FbsModelProperties.createDateTimeValuesVector(
      b,
      []
    );

    FbsModelProperties.startProductBindingsVector(b, 0);
    const productBindingsOff = b.endVector();
    FbsModelProperties.startProductsVector(b, 0);
    const productsOff = b.endVector();
    FbsModelProperties.startHistoriesVector(b, 0);
    const historiesOff = b.endVector();

    FbsModelProperties.startModelProperties(b);
    FbsModelProperties.addProductBindings(b, productBindingsOff);
    FbsModelProperties.addProducts(b, productsOff);
    FbsModelProperties.addOwners(b, ownersOff);
    FbsModelProperties.addHistories(b, historiesOff);
    FbsModelProperties.addNames(b, namesOff);
    FbsModelProperties.addDescriptions(b, descriptionsOff);
    FbsModelProperties.addObjectTypes(b, objectTypesOff);
    FbsModelProperties.addPropertySetBindings(b, bindingsOff);
    FbsModelProperties.addPropertySetNames(b, propertySetNamesOff);
    FbsModelProperties.addPropertyNames(b, propertyNamesOff);
    FbsModelProperties.addDefinitions(b, definitionsOff);
    FbsModelProperties.addLengthMeasures(b, lengthMeasuresOff);
    FbsModelProperties.addAreaMeasures(b, areaMeasuresOff);
    FbsModelProperties.addVolumeMeasures(b, volumeMeasuresOff);
    FbsModelProperties.addMassMeasures(b, massMeasuresOff);
    FbsModelProperties.addAngleMeasures(b, angleMeasuresOff);
    FbsModelProperties.addStringValues(b, stringValuesOff);
    FbsModelProperties.addDoubleValues(b, doubleValuesOff);
    FbsModelProperties.addDateTimeValues(b, dateTimeValuesOff);

    return FbsModelProperties.endModelProperties(b);
  }

  private buildEntities(b: Builder): Offset {
    const strIdOffsets = this.stringIdentifiers.map((id) => b.createString(id));
    const stringIdentifiersOff = FbsModelEntities.createStringIdentifiersVector(
      b,
      strIdOffsets
    );
    const dwgHandleOff = FbsModelEntities.createDwgHandleIdentifiersVector(
      b,
      []
    );
    const entityClassOffsets =
      this.entityClasses.length > 0
        ? this.entityClasses.map((c) => b.createString(c))
        : [b.createString('Class')];
    const entityClassesOff = FbsModelEntities.createEntityClassesVector(
      b,
      entityClassOffsets
    );

    FbsModelEntities.startHierarchiesVector(b, 0);
    const hierarchiesOff = b.endVector();
    FbsModelEntities.startGuidIdentifiersVector(b, 0);
    const guidIdentifiersOff = b.endVector();
    FbsModelEntities.startSpatialHashIdentifiersVector(b, 0);
    const spatialHashOff = b.endVector();

    // Entities vector
    FbsModelEntities.startEntitiesVector(b, this.entities.length);
    for (let i = this.entities.length - 1; i >= 0; i--) {
      const e = this.entities[i];
      FbsEntity.createEntity(
        b,
        e.idType,
        e.idIndex,
        e.classIndex,
        e.globalOrigin.x,
        e.globalOrigin.y,
        e.globalOrigin.z,
        e.xAxis.x,
        e.xAxis.y,
        e.xAxis.z,
        e.yAxis.x,
        e.yAxis.y,
        e.yAxis.z
      );
    }
    const entitiesOff = b.endVector();

    FbsModelEntities.startModelEntities(b);
    FbsModelEntities.addEntities(b, entitiesOff);
    FbsModelEntities.addHierarchies(b, hierarchiesOff);
    FbsModelEntities.addGuidIdentifiers(b, guidIdentifiersOff);
    FbsModelEntities.addStringIdentifiers(b, stringIdentifiersOff);
    FbsModelEntities.addSpatialHashIdentifiers(b, spatialHashOff);
    FbsModelEntities.addDwgHandleIdentifiers(b, dwgHandleOff);
    FbsModelEntities.addEntityClasses(b, entityClassesOff);

    return FbsModelEntities.endModelEntities(b);
  }

  private buildGeometry(b: Builder): Offset {
    // ── GraphicsContainers ────────────────────────────────────────────
    const gcOffsets = this.graphicsContainers.map((gc) => {
      FbsGraphicsContainer.startLineSegmentsVector(b, gc.lineSegments.length);
      for (let i = gc.lineSegments.length - 1; i >= 0; i--) {
        const seg = gc.lineSegments[i];
        FbsLine3f.createLine3f(
          b,
          seg.start.x,
          seg.start.y,
          seg.start.z,
          seg.end.x,
          seg.end.y,
          seg.end.z
        );
      }
      const lineSegsOff = b.endVector();

      FbsGraphicsContainer.startArcsVector(b, gc.arcs.length);
      for (let i = gc.arcs.length - 1; i >= 0; i--) {
        const arc = gc.arcs[i];
        FbsArc3f.createArc3f(
          b,
          arc.start.x,
          arc.start.y,
          arc.start.z,
          arc.mid.x,
          arc.mid.y,
          arc.mid.z,
          arc.end.x,
          arc.end.y,
          arc.end.z,
          arc.normal.x,
          arc.normal.y
        );
      }
      const arcsOff = b.endVector();

      FbsGraphicsContainer.startGraphicsContainer(b);
      FbsGraphicsContainer.addLineSegments(b, lineSegsOff);
      FbsGraphicsContainer.addArcs(b, arcsOff);
      return FbsGraphicsContainer.endGraphicsContainer(b);
    });
    const graphicsContainersOff =
      FbsModelGeometry.createGraphicsContainersVector(b, gcOffsets);

    // ── BillboardTexts ────────────────────────────────────────────────
    const btOffsets = this.billboardTexts.map((bt) => {
      const textOff = b.createString(bt.text);
      FbsBillboardText.startBillboardText(b);
      FbsBillboardText.addTextStyleId(b, bt.textStyleId);
      FbsBillboardText.addText(b, textOff);
      return FbsBillboardText.endBillboardText(b);
    });
    const billboardTextsOff = FbsModelGeometry.createBillboardTextsVector(
      b,
      btOffsets
    );

    // ── TriangleMeshes ──────────────────────────────────────────────────
    const tmOffsets = this.triangleMeshes.map((tm) => {
      FbsTriangleMesh.startPositionsVector(b, tm.positions.length);
      for (let i = tm.positions.length - 1; i >= 0; i--) {
        FbsVec3f.createVec3f(
          b,
          tm.positions[i].x,
          tm.positions[i].y,
          tm.positions[i].z
        );
      }
      const posOff = b.endVector();

      FbsTriangleMesh.startNormalsVector(b, tm.normals.length);
      for (let i = tm.normals.length - 1; i >= 0; i--) {
        FbsVec3f.createVec3f(
          b,
          tm.normals[i].x,
          tm.normals[i].y,
          tm.normals[i].z
        );
      }
      const normOff = b.endVector();

      FbsTriangleMesh.startVerticesVector(b, tm.vertices.length);
      for (let i = tm.vertices.length - 1; i >= 0; i--) {
        FbsIndexedVertex.createIndexedVertex(
          b,
          tm.vertices[i].positionIndex,
          tm.vertices[i].normalIndex
        );
      }
      const vertOff = b.endVector();

      const idxOff = FbsTriangleMesh.createIndicesVector(b, tm.indices);

      FbsTriangleMesh.startTriangleMesh(b);
      FbsTriangleMesh.addPositions(b, posOff);
      FbsTriangleMesh.addNormals(b, normOff);
      FbsTriangleMesh.addVertices(b, vertOff);
      FbsTriangleMesh.addIndices(b, idxOff);
      return FbsTriangleMesh.endTriangleMesh(b);
    });
    const triangleMeshesOff = FbsModelGeometry.createTriangleMeshesVector(
      b,
      tmOffsets
    );

    // ── TextStyles ────────────────────────────────────────────────────
    const fontOffsets = this.textStyles.map((ts) => b.createString(ts.font));
    const tsOffsets = this.textStyles.map((ts, i) => {
      FbsTextStyle.startTextStyle(b);
      FbsTextStyle.addFont(b, fontOffsets[i]);
      FbsTextStyle.addSize(b, ts.size);
      FbsTextStyle.addOptions(b, ts.options);
      FbsTextStyle.addVerticalAlign(b, ts.verticalAlign);
      FbsTextStyle.addHorizontalAlign(b, ts.horizontalAlign);
      FbsTextStyle.addRotationAngle(b, ts.rotationAngle);
      FbsTextStyle.addLayout(b, ts.layout as LayoutType);
      FbsTextStyle.addAlignment(b, ts.alignment as AlignmentType);
      FbsTextStyle.addMaxAutoScale(b, ts.maxAutoScale);
      FbsTextStyle.addVerticalDistanceToObject(b, ts.verticalDistanceToObject);
      return FbsTextStyle.endTextStyle(b);
    });
    const textStylesOff = FbsModelGeometry.createTextStylesVector(b, tsOffsets);

    // ── Placements ────────────────────────────────────────────────────
    FbsModelGeometry.startLocalPlacementsVector(b, this.placements.length);
    for (let i = this.placements.length - 1; i >= 0; i--) {
      const p = this.placements[i];
      FbsPlacement3.createPlacement3(
        b,
        p.origin.x,
        p.origin.y,
        p.origin.z,
        p.xAxis.x,
        p.xAxis.y,
        p.xAxis.z,
        p.yAxis.x,
        p.yAxis.y,
        p.yAxis.z
      );
    }
    const placementsOff = b.endVector();

    // ── SurfaceMaterials ──────────────────────────────────────────────
    FbsModelGeometry.startSurfaceMaterialsVector(b, this.materials.length);
    for (let i = this.materials.length - 1; i >= 0; i--) {
      const m = this.materials[i];
      FbsSurfaceMaterial.createSurfaceMaterial(
        b,
        m.r,
        m.g,
        m.b,
        m.a,
        m.textureId,
        m.sidedness as Sidedness,
        m.lineStyle as LineStyle,
        m.metallic,
        m.roughness
      );
    }
    const surfaceMaterialsOff = b.endVector();

    // ── Layers ────────────────────────────────────────────────────────
    const layerOffsets =
      this.layers.length > 0
        ? this.layers.map((l) => b.createString(l))
        : [b.createString('Layer')];
    const layersOff = FbsModelGeometry.createLayersVector(b, layerOffsets);

    // ── Instances ─────────────────────────────────────────────────────
    FbsModelGeometry.startInstancesVector(b, this.instances.length);
    for (let i = this.instances.length - 1; i >= 0; i--) {
      const inst = this.instances[i];
      FbsGeometryInstance.createGeometryInstance(
        b,
        inst.localPlacementId,
        inst.entityId,
        inst.definitionId,
        inst.surfaceMaterialId,
        inst.layerId
      );
    }
    const instancesOff = b.endVector();

    // ── Definitions ───────────────────────────────────────────────────
    FbsModelGeometry.startDefinitionsVector(b, this.definitions.length);
    for (let i = this.definitions.length - 1; i >= 0; i--) {
      const def = this.definitions[i];
      FbsGeometryDefinition.createGeometryDefinition(
        b,
        def.type,
        def.typeIndex,
        def.bbMin.x,
        def.bbMin.y,
        def.bbMin.z,
        def.bbMax.x,
        def.bbMax.y,
        def.bbMax.z
      );
    }
    const definitionsOff = b.endVector();

    // ── Empty vectors for unused geometry types ───────────────────────
    const emptyVec = (create: (b: Builder, d: Offset[]) => Offset): Offset =>
      create(b, []);

    const alignmentCurvesOff = emptyVec(
      FbsModelGeometry.createAlignmentCurvesVector
    );
    const gridLineContainersOff = emptyVec(
      FbsModelGeometry.createGridLineContainersVector
    );
    const billboardsOff = emptyVec(FbsModelGeometry.createBillboardsVector);
    const boltsOff = emptyVec(FbsModelGeometry.createBoltsVector);
    const bRepsOff = emptyVec(FbsModelGeometry.createBRepsVector);
    const bRepNormOff = emptyVec(FbsModelGeometry.createBRepWithNormalsVector);
    const bRepUvOff = emptyVec(FbsModelGeometry.createBRepWithUvsVector);
    const coordGeoOff = emptyVec(
      FbsModelGeometry.createCoordinateGeometryPointsVector
    );
    const sweptOff = emptyVec(FbsModelGeometry.createSweptDiskSolidsVector);
    const texMeshOff = emptyVec(
      FbsModelGeometry.createTexturedTriangleMeshesVector
    );
    const texMesh8Off = emptyVec(
      FbsModelGeometry.createTexturedTriangleMeshes8Vector
    );
    const texturesOff = emptyVec(FbsModelGeometry.createTexturesVector);
    // triMeshOff is built above from this.triangleMeshes
    // (triangleMeshesOff is the real offset; triMeshOff alias for the assembly)
    const triMeshOff = triangleMeshesOff;
    const triMesh8Off = emptyVec(FbsModelGeometry.createTriangleMeshes8Vector);

    // TexturedBillboards (struct vector, not table vector)
    FbsModelGeometry.startTexturedBillboardsVector(b, 0);
    const texBbOff = b.endVector();

    // Layer default visibility
    const layerVisibility =
      this.layerVisibility.length > 0
        ? this.layerVisibility
        : Array.from<boolean>({ length: Math.max(this.layers.length, 1) }).fill(
            true
          );
    const layerVisibilityOff =
      FbsModelGeometry.createLayerDefaultVisibilityVector(b, layerVisibility);

    // ── Assemble ModelGeometry ────────────────────────────────────────
    FbsModelGeometry.startModelGeometry(b);
    FbsModelGeometry.addLocalPlacements(b, placementsOff);
    FbsModelGeometry.addSurfaceMaterials(b, surfaceMaterialsOff);
    FbsModelGeometry.addLayers(b, layersOff);
    FbsModelGeometry.addDefinitions(b, definitionsOff);
    FbsModelGeometry.addInstances(b, instancesOff);
    FbsModelGeometry.addTextures(b, texturesOff);
    FbsModelGeometry.addBReps(b, bRepsOff);
    FbsModelGeometry.addTriangleMeshes(b, triMeshOff);
    FbsModelGeometry.addTriangleMeshes8(b, triMesh8Off);
    FbsModelGeometry.addTexturedTriangleMeshes(b, texMeshOff);
    FbsModelGeometry.addTexturedTriangleMeshes8(b, texMesh8Off);
    FbsModelGeometry.addSweptDiskSolids(b, sweptOff);
    FbsModelGeometry.addBRepWithUvs(b, bRepUvOff);
    FbsModelGeometry.addBRepWithNormals(b, bRepNormOff);
    FbsModelGeometry.addGraphicsContainers(b, graphicsContainersOff);
    FbsModelGeometry.addAlignmentCurves(b, alignmentCurvesOff);
    FbsModelGeometry.addGridLineContainers(b, gridLineContainersOff);
    FbsModelGeometry.addCoordinateGeometryPoints(b, coordGeoOff);
    FbsModelGeometry.addBillboardTexts(b, billboardTextsOff);
    FbsModelGeometry.addTextStyles(b, textStylesOff);
    FbsModelGeometry.addBillboards(b, billboardsOff);
    FbsModelGeometry.addLayerDefaultVisibility(b, layerVisibilityOff);
    FbsModelGeometry.addTexturedBillboards(b, texBbOff);
    FbsModelGeometry.addBolts(b, boltsOff);

    return FbsModelGeometry.endModelGeometry(b);
  }
}
